/**
 * DatasetsManagementSimplified - Standalone Datasets Management Component
 *
 * Manages datasets with CRUD operations using DataCard for display.
 * Simplified version that only handles datasets (not other entities).
 */

import { useState, useEffect } from 'react';
import { Button, Table, Spinner, Badge } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import DataCard from '@/components/common/DataCard';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import DatasetsModal from './modals/DatasetsModal';
import type { Dataset, DataType, Study, Clinic, Device } from '@/types/associations';

interface User {
  id: number;
  email?: string;
  name?: string;
}

interface UserGroup {
  id: number;
  name?: string;
}

interface Role {
  id: number;
  name?: string;
}

export default function DatasetsManagementSimplified() {
  // Entity state
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [dataTypes, setDataTypes] = useState<DataType[]>([]);
  const [studies, setStudies] = useState<Study[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Refresh states
  const [isRefreshingDataTypes, setIsRefreshingDataTypes] = useState(false);

  // Modal state
  const [modalState, setModalState] = useState<{ show: boolean; editing: Dataset | null }>({
    show: false,
    editing: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [datasetsRes, dataTypesRes, studiesRes, clinicsRes, devicesRes, usersRes, groupsRes, rolesRes] = await Promise.all([
        apiClient.getAdminDatasets({ skip: 0, limit: 1000 }),
        apiClient.getAdminDataTypes(),
        apiClient.getAdminStudies(),
        apiClient.getAdminClinics(),
        apiClient.getAdminDevices(),
        apiClient.getAdminUsers({ skip: 0, limit: 1000 }),
        apiClient.getAdminGroups({ skip: 0, limit: 1000 }),
        apiClient.getAdminRoles(),
      ]);

      if (!datasetsRes.error && datasetsRes.data) {
        const data = datasetsRes.data as { datasets?: Dataset[] } | Dataset[];
        setDatasets(Array.isArray(data) ? data : data.datasets || []);
      }
      if (!dataTypesRes.error && dataTypesRes.data) {
        setDataTypes(dataTypesRes.data as DataType[]);
      }
      if (!studiesRes.error && studiesRes.data) {
        setStudies(studiesRes.data as Study[]);
      }
      if (!clinicsRes.error && clinicsRes.data) {
        setClinics(clinicsRes.data as Clinic[]);
      }
      if (!devicesRes.error && devicesRes.data) {
        setDevices(devicesRes.data as Device[]);
      }
      if (!usersRes.error && usersRes.data) {
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      }
      if (!groupsRes.error && groupsRes.data) {
        setUserGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      }
      if (!rolesRes.error && rolesRes.data) {
        setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      }
    } catch (error) {
      toast.error('Failed to load datasets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshDataTypes = async () => {
    setIsRefreshingDataTypes(true);
    try {
      const response = await apiClient.getAdminDataTypes();
      if (!response.error && response.data) {
        setDataTypes(response.data as DataType[]);
        toast.success('Data types refreshed');
      }
    } catch (error) {
      toast.error('Failed to refresh data types');
    } finally {
      setIsRefreshingDataTypes(false);
    }
  };

  // CRUD handlers
  const handleSave = async (data: Partial<Dataset> & { data_type_id?: number }) => {
    const datasetPayload = {
      name: data.name,
      description: data.description,
      data_type_id: data.data_type_id,
    };

    if (modalState.editing) {
      const response = await apiClient.updateAdminDataset(modalState.editing.id, datasetPayload);
      if (response.error) throw new Error(response.error);
      toast.success('Dataset updated successfully');
    } else {
      if (!datasetPayload.data_type_id) {
        throw new Error('Data type is required');
      }
      const response = await apiClient.createAdminDataset({
        name: datasetPayload.name || '',
        data_type_id: datasetPayload.data_type_id,
      });
      if (response.error) throw new Error(response.error);
      toast.success('Dataset created successfully');
    }
    await loadData();
  };

  const handleDelete = (id: number) => {
    const dataset = datasets.find((d) => d.id === id);
    setConfirmModal({
      show: true,
      title: 'Delete Dataset',
      message: `Are you sure you want to delete "${dataset?.name || `Dataset ${id}`}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await apiClient.deleteAdminDataset(id);
          if (response.error) throw new Error(response.error);
          toast.success('Dataset deleted successfully');
          setSelectedId(null);
          await loadData();
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to delete dataset');
        } finally {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }
      },
    });
  };

  // Helper function to get data type name
  const getDataTypeName = (dataTypeId: number | undefined): string => {
    if (!dataTypeId) return '-';
    const dataType = dataTypes.find((dt) => dt.id === dataTypeId);
    return dataType?.name || `DataType ${dataTypeId}`;
  };

  // Table rendering
  const renderTable = (items: Dataset[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Data Type</th>
          <th>Description</th>
          <th>Created</th>
        </tr>
      </thead>
      <tbody>
        {items.map((dataset) => (
          <tr
            key={dataset.id}
            onClick={() => setSelectedId(selectedId === dataset.id ? null : dataset.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedId === dataset.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><strong>{dataset.name || `Dataset ${dataset.id}`}</strong></td>
            <td><Badge bg="info">{getDataTypeName(dataset.data_type_id)}</Badge></td>
            <td className="text-muted small" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dataset.description || '-'}
            </td>
            <td className="text-muted small">
              {dataset.created_at ? new Date(dataset.created_at).toLocaleDateString() : '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading datasets...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Datasets Management</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Manage datasets and their associated policies
          </p>
        </div>
      </div>

      <DataCard
        cardKey="datasets"
        title="Datasets"
        badgeCount={datasets.length}
        data={datasets}
        itemsPerPage={10}
        searchPlaceholder="Search datasets..."
        filterFunction={(item, searchTerm) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          return (
            (item.name || '').toLowerCase().includes(term) ||
            (item.description || '').toLowerCase().includes(term) ||
            getDataTypeName(item.data_type_id).toLowerCase().includes(term)
          );
        }}
        headerActions={
          <>
            {selectedId !== null && (
              <>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    const dataset = datasets.find((d) => d.id === selectedId);
                    if (dataset) setModalState({ show: true, editing: dataset });
                  }}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    if (selectedId !== null) handleDelete(selectedId);
                  }}
                  className="me-2"
                >
                  Delete
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModalState({ show: true, editing: null })}
            >
              Create
            </Button>
          </>
        }
        renderTable={renderTable}
        onRefresh={loadData}
        maxHeight="600px"
        emptyMessage="No datasets found. Create your first dataset."
      />

      {/* Dataset Modal */}
      <DatasetsModal
        show={modalState.show}
        dataset={modalState.editing}
        studies={studies}
        clinics={clinics}
        devices={devices}
        dataTypes={dataTypes}
        users={users}
        userGroups={userGroups}
        roles={roles}
        onHide={() => setModalState({ show: false, editing: null })}
        onSave={handleSave}
        onRefresh={loadData}
        onRefreshDataTypes={handleRefreshDataTypes}
        isRefreshingDataTypes={isRefreshingDataTypes}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}
